import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Typography, CircularProgress,
  Grid, Card, CardContent, CardMedia, CardActions,
  Box, Chip, InputAdornment, IconButton, Divider,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { searchAPI, booksAPI } from '../../services/api';

const GoogleBookSearch = ({ open, onClose, onBookAdd }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(0);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      setSearching(true);
      setError(null);
      const response = await searchAPI.searchGoogleBooks(searchQuery);
      
      if (response.data && response.data.items) {
        setSearchResults(response.data.items);
        setTotalResults(response.data.totalItems || response.data.items.length);
      } else {
        setSearchResults([]);
        setTotalResults(0);
        setError('No results found');
      }
    } catch (err) {
      console.error('Error searching Google Books:', err);
      setError(err.response?.data?.message || 'Error searching Google Books');
      setSearchResults([]);
      setTotalResults(0);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectBook = (book) => {
    setSelectedBook(book);
  };

  const handleAddBook = async () => {
    if (!selectedBook) return;

    try {
      setAdding(true);
      setError(null);

      // Create the book using isbn or book data
      let response;
      if (selectedBook.isbn) {
        response = await booksAPI.createBookFromHybridSources({
          isbn: selectedBook.isbn
        });
      } else {
        // Create book with available data if no ISBN
        response = await booksAPI.createBook({
          title: selectedBook.title,
          author: selectedBook.authors || 'Unknown',
          cover: selectedBook.coverImage || '',
          description: selectedBook.description || '',
          status: 'draft',
          categoryIds: [],
          audioUrls: {},
          textTypes: []
        });
      }

      if (response.data && onBookAdd) {
        onBookAdd(response.data.book || response.data);
      }

      // Reset and close
      setSelectedBook(null);
      setSearchQuery('');
      setSearchResults([]);
      onClose();
    } catch (err) {
      console.error('Error adding book:', err);
      setError(err.response?.data?.message || 'Error adding book');
    } finally {
      setAdding(false);
    }
  };

  const renderBookCard = (book) => {
    const isSelected = selectedBook && selectedBook.id === book.id;
    
    return (
      <Card 
        variant="outlined" 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%',
          cursor: 'pointer',
          border: isSelected ? 2 : 1,
          borderColor: isSelected ? 'primary.main' : 'divider',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 2
          }
        }}
        onClick={() => handleSelectBook(book)}
      >
        <CardMedia
          component="img"
          height="160"
          image={book.coverImage || 'https://placehold.co/120x180?text=No+Cover'}
          alt={book.title}
          sx={{ objectFit: 'contain', p: 1 }}
        />
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          <Typography variant="subtitle1" gutterBottom noWrap>
            {book.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {book.authors || 'Unknown author'}
          </Typography>
          {book.isbn && (
            <Chip 
              label={`ISBN: ${book.isbn}`}
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
            />
          )}
        </CardContent>
        <CardActions>
          <Button 
            size="small" 
            color="primary"
            startIcon={<AddIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectBook(book);
              handleAddBook();
            }}
          >
            Add to Library
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Search Google Books</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <form onSubmit={handleSearch}>
          <TextField
            fullWidth
            label="Search for books"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            margin="normal"
            placeholder="Enter title, author, or ISBN"
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton 
                    type="submit"
                    disabled={!searchQuery.trim() || searching}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </form>
        
        {searching && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        {!searching && searchResults.length > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={1}>
              <Typography variant="subtitle1">
                {totalResults} results found
              </Typography>
              {selectedBook && (
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleAddBook}
                  disabled={adding}
                  startIcon={adding ? <CircularProgress size={16} /> : <AddIcon />}
                >
                  {adding ? 'Adding...' : 'Add Selected Book'}
                </Button>
              )}
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              {searchResults.map((book) => (
                <Grid item xs={12} sm={6} md={3} key={book.id}>
                  {renderBookCard(book)}
                </Grid>
              ))}
            </Grid>
          </>
        )}
        
        {!searching && searchResults.length === 0 && searchQuery && !error && (
          <Box textAlign="center" my={4}>
            <Typography variant="body1" color="text.secondary">
              No books found matching your search.
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Try a different search term or check the spelling.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoogleBookSearch;
